import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  Bold, 
  Italic, 
  Underline, 
  AlignLeft, 
  AlignCenter, 
  AlignRight,
  List,
  ListOrdered,
  Undo,
  Redo,
  Type,
  Plus,
  Trash2,
  ChevronLeft,
  ChevronRight,
  Users,
  Wifi,
  WifiOff
} from 'lucide-react';
import { supabase } from '../../../shared/lib/supabase';
import { useAuth } from '../../auth/contexts/AuthContext';
import { useUserProfile } from '../../../shared/hooks/useUserProfile';
import { PresenceIndicator } from '../../../shared/components/ui/PresenceIndicator';
import { debounce } from 'lodash';

interface Page {
  id: string;
  content: string;
}

interface NotebookEditorProps {
  initialContent?: {
    pages?: Page[];
  };
  onChange: (content: any) => void;
  entryId?: string;
  projectId?: string;
  singlePageMode?: boolean;
}

// Simplified interface without avatar_url
interface PresenceUser {
  userId: string;
  userName: string;
  userColor: string;
  online_at: string;
}

interface CursorPosition {
  userId: string;
  userName: string;
  userColor: string;
  pageIndex: number;
  x: number;
  y: number;
  textOffset: number;
  containerText: string;
  timestamp: string;
}

interface CollaborationState {
  type: 'insert' | 'delete' | 'format' | 'full_sync';
  pageIndex: number;
  position: number;
  content?: string;
  length?: number;
  userId: string;
  timestamp: string;
}

export const NotebookEditor: React.FC<NotebookEditorProps> = ({ 
  initialContent, 
  onChange,
  entryId,
  projectId,
  singlePageMode = false
}) => {
  const { user } = useAuth();
  const { profile } = useUserProfile();
  const [pages, setPages] = useState<Page[]>(() => {
    if (initialContent?.pages && initialContent.pages.length > 0) {
      return initialContent.pages;
    }
    return [{
      id: `page-${Date.now()}`,
      content: ''
    }];
  });
  
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [fontSize, setFontSize] = useState<'normal' | 'header2' | 'header1'>('normal');
  const editorRef = useRef<HTMLDivElement>(null);
  const [history, setHistory] = useState<string[][]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [isUpdatingContent, setIsUpdatingContent] = useState(false);
  const [activeFormats, setActiveFormats] = useState({
    bold: false,
    italic: false,
    underline: false,
    unorderedList: false,
    orderedList: false
  });

  // Real-time collaboration state - SIMPLIFIED
  const [presenceUsers, setPresenceUsers] = useState<PresenceUser[]>([]);
  const [cursors, setCursors] = useState<CursorPosition[]>([]);
  const [connectionStatus, setConnectionStatus] = useState<'connected' | 'reconnecting' | 'disconnected'>('disconnected');
  const [userColor] = useState(() => {
    const colors = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FECA57', '#FF9FF3', '#A8E6CF', '#FFD93D'];
    return colors[Math.floor(Math.random() * colors.length)];
  });
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isTyping, setIsTyping] = useState(false);

  const channelRef = useRef<any>(null);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastCursorPosition = useRef<number>(0);

  // Enhanced real-time collaboration setup with better connection handling
  useEffect(() => {
    if (!entryId || !user) {
      setConnectionStatus('disconnected');
      return;
    }

    let reconnectAttempts = 0;
    const maxReconnectAttempts = 5;
    let reconnectTimeout: NodeJS.Timeout;

    const setupChannel = async () => {
      try {
        setConnectionStatus('reconnecting');
        
        // Clean up previous channel
        if (channelRef.current) {
          await channelRef.current.unsubscribe();
          channelRef.current = null;
        }

        const channel = supabase.channel(`notebook_${entryId}`, {
          config: {
            broadcast: { self: false, ack: true },
            presence: { key: user.id }
          }
        });

        channel
          .on('broadcast', { event: 'content_change' }, (payload: any) => {
            if (payload.payload.userId !== user.id) {
              console.log('📝 Content updated by another user');
              handleRemoteContentChange(payload.payload);
            }
          })
          .on('broadcast', { event: 'cursor_move' }, (payload: any) => {
            if (payload.payload.userId !== user.id) {
              handleRemoteCursorMove(payload.payload);
            }
          })
          .on('broadcast', { event: 'typing_indicator' }, (payload: any) => {
            if (payload.payload.userId !== user.id) {
              handleTypingIndicator(payload.payload);
            }
          })
          .on('presence', { event: 'sync' }, () => {
            const state = channel.presenceState();
            const users: PresenceUser[] = [];
            
            Object.keys(state).forEach(userId => {
              const presences = state[userId] as any[];
              if (presences.length > 0 && userId !== user.id) {
                const presence = presences[0];
                users.push({
                  userId,
                  userName: presence.userName || 'Unknown User',
                  userColor: presence.userColor || '#6B7280',
                  online_at: presence.online_at
                });
              }
            });
            
            // Always include current user for proper count
            if (user?.id && profile) {
              const currentUser: PresenceUser = {
                userId: user.id,
                userName: profile.first_name && profile.last_name 
                  ? `${profile.first_name} ${profile.last_name}` 
                  : user.email || 'Unknown User',
                userColor: userColor,
                online_at: new Date().toISOString()
              };
              setPresenceUsers([currentUser, ...users]);
            } else {
              setPresenceUsers(users);
            }
            setConnectionStatus('connected');
            reconnectAttempts = 0; // Reset on successful connection
          })
          .on('presence', { event: 'join' }, ({ newPresences }: any) => {
            console.log('👋 User joined:', newPresences);
            // Send current content to new user after small delay
            setTimeout(() => {
              if (pages.length > 0) {
                broadcastContentChange(pages, currentPageIndex, 'full_sync');
              }
            }, 1000);
          })
          .on('presence', { event: 'leave' }, ({ leftPresences }: any) => {
            console.log('👋 User left:', leftPresences);
            const leftUserIds = leftPresences.map((p: any) => p.userId);
            setCursors(prev => prev.filter(c => !leftUserIds.includes(c.userId)));
          });

        // Subscribe with error handling
        const subscribeResult = await new Promise((resolve) => {
          const subscription = channel.subscribe((status: string, err?: any) => {
            if (err) {
              console.error('Subscription error:', err);
              resolve('ERROR');
            } else {
              resolve(status);
            }
          });
        });
        
        if (subscribeResult === 'SUBSCRIBED') {
          // Track presence - SIMPLIFIED without avatar_url
          const trackResult = await channel.track({
            userId: user.id,
            userName: `${profile?.first_name || ''} ${profile?.last_name || ''}`.trim() || user.email?.split('@')[0] || 'Anonymous',
            userColor: userColor,
            online_at: new Date().toISOString()
          });
          
          if (trackResult === 'ok') {
            setConnectionStatus('connected');
            channelRef.current = channel;
            reconnectAttempts = 0;
            console.log('✅ Successfully connected to real-time channel');
          } else {
            throw new Error('Failed to track presence');
          }
        } else {
          throw new Error(`Subscription failed: ${subscribeResult}`);
        }

      } catch (error) {
        console.error('Connection setup failed:', error);
        setConnectionStatus('disconnected');
        
        // Exponential backoff retry
        if (reconnectAttempts < maxReconnectAttempts) {
          reconnectAttempts++;
          const delay = Math.min(1000 * Math.pow(2, reconnectAttempts), 10000);
          console.log(`🔄 Retrying connection in ${delay}ms (attempt ${reconnectAttempts})`);
          
          reconnectTimeout = setTimeout(() => {
            setupChannel();
          }, delay);
        } else {
          console.error('❌ Max reconnection attempts reached');
          setConnectionStatus('disconnected');
        }
      }
    };

    // Initial connection
    setupChannel();

    // Cleanup function
    return () => {
      if (reconnectTimeout) {
        clearTimeout(reconnectTimeout);
      }
      if (channelRef.current) {
        channelRef.current.unsubscribe();
        channelRef.current = null;
      }
    };
  }, [entryId, user, userColor, currentPageIndex, profile?.first_name, profile?.last_name]);

  // Handle remote content changes with operational transformation
  const handleRemoteContentChange = useCallback((payload: CollaborationState) => {
    if (payload.type === 'full_sync') {
      // Handle full document sync
      const updatedPages = payload.content ? JSON.parse(payload.content) : [];
      setPages(updatedPages);
      
      if (payload.pageIndex === currentPageIndex && editorRef.current) {
        setIsUpdatingContent(true);
        editorRef.current.innerHTML = updatedPages[currentPageIndex]?.content || '';
        setIsUpdatingContent(false);
      }
    } else {
      // Handle incremental changes (could be enhanced with OT)
      const updatedPages = [...pages];
      if (updatedPages[payload.pageIndex] && payload.content) {
        updatedPages[payload.pageIndex].content = payload.content;
        setPages(updatedPages);
        
        if (payload.pageIndex === currentPageIndex && editorRef.current) {
          setIsUpdatingContent(true);
          editorRef.current.innerHTML = payload.content;
          setIsUpdatingContent(false);
        }
      }
    }
  }, [pages, currentPageIndex]);

  // Handle remote cursor movements with accurate positioning
  const handleRemoteCursorMove = useCallback((payload: CursorPosition) => {
    setCursors(prev => {
      const filtered = prev.filter(c => c.userId !== payload.userId);
      return [...filtered, payload];
    });

    // Remove cursor after 5 seconds of inactivity
    setTimeout(() => {
      setCursors(prev => prev.filter(c => 
        c.userId !== payload.userId || 
        new Date().getTime() - new Date(c.timestamp).getTime() < 5000
      ));
    }, 5000);
  }, []);

  // Handle typing indicators
  const handleTypingIndicator = useCallback((payload: any) => {
    if (payload.isTyping) {
      // Update presence users with typing indicator (temporary approach)
      setPresenceUsers(prev => prev.map(user => 
        user.userId === payload.userId 
          ? { ...user, isTyping: true } as any
          : user
      ));
      
      // Clear typing indicator after 3 seconds
      setTimeout(() => {
        setPresenceUsers(prev => prev.map(user => 
          user.userId === payload.userId 
            ? { ...user, isTyping: false } as any
            : user
        ));
      }, 3000);
    }
  }, []);

  // Enhanced broadcast content change with better error handling
  const broadcastContentChange = useCallback((updatedPages: Page[], pageIndex: number, type: CollaborationState['type'] = 'full_sync') => {
    if (channelRef.current && user && connectionStatus === 'connected') {
      const payload: CollaborationState = {
        type,
        pageIndex,
        position: lastCursorPosition.current,
        content: type === 'full_sync' ? JSON.stringify(updatedPages) : updatedPages[pageIndex]?.content,
        userId: user.id,
        timestamp: new Date().toISOString()
      };

      try {
        channelRef.current.send({
          type: 'broadcast',
          event: 'content_change',
          payload
        });
        console.log('📡 Broadcasted content change');
      } catch (error) {
        console.error('Failed to broadcast content change:', error);
        setConnectionStatus('disconnected');
      }
    } else if (!channelRef.current || connectionStatus !== 'connected') {
      console.warn('⚠️ Cannot broadcast - not connected to real-time channel');
    }
  }, [user, connectionStatus]);

  // Enhanced cursor broadcasting with accurate position tracking
  const broadcastCursor = useCallback((selection: Selection) => {
    if (channelRef.current && user && editorRef.current) {
      if (selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        
        // Get accurate cursor position using Range API
        const rect = range.getBoundingClientRect();
        const editorRect = editorRef.current.getBoundingClientRect();
        
        // Calculate relative position within the editor
        const relativeX = rect.left - editorRect.left;
        const relativeY = rect.top - editorRect.top;
        
        // Get text offset for more precise positioning
        const textOffset = range.startOffset;
        const containerText = range.startContainer.textContent || '';
        
        const payload = {
          userId: user.id,
          userName: `${profile?.first_name || ''} ${profile?.last_name || ''}`.trim() || user.email?.split('@')[0] || 'Anonymous',
          userColor: userColor,
          pageIndex: currentPageIndex,
          // Accurate positioning data
          x: relativeX,
          y: relativeY,
          textOffset: textOffset,
          containerText: containerText.substring(0, 50), // Context for positioning
          timestamp: new Date().toISOString()
        };

        channelRef.current.send({
          type: 'broadcast',
          event: 'cursor_move',
          payload
        });
      }
    }
  }, [user, userColor, currentPageIndex, profile?.first_name, profile?.last_name]);

  // Broadcast typing indicator
  const broadcastTyping = useCallback((isTyping: boolean) => {
    if (channelRef.current && user) {
      channelRef.current.send({
        type: 'broadcast',
        event: 'typing_indicator',
        payload: {
          userId: user.id,
          isTyping,
          timestamp: new Date().toISOString()
        }
      });
    }
  }, [user]);

  // Auto-save function with immediate saves and better error handling
  const autoSave = useCallback(
    debounce(async (content: any) => {
      if (!onChange) return;
      
      setIsSaving(true);
      try {
        console.log('💾 Auto-saving content...');
        await onChange(content);
        setLastSaved(new Date());
        console.log('✅ Auto-save successful');
      } catch (error) {
        console.error('❌ Auto-save failed:', error);
        // Retry once after a short delay
        setTimeout(() => {
          try {
            onChange(content);
            setLastSaved(new Date());
          } catch (retryError) {
            console.error('❌ Auto-save retry failed:', retryError);
          }
        }, 1000);
      } finally {
        setIsSaving(false);
      }
    }, 500), // Reduced debounce time for faster saves
    [onChange]
  );

  // Immediate save function for critical operations
  const immediateAutoSave = useCallback((content: any) => {
    if (!onChange) return;
    
    setIsSaving(true);
    try {
      onChange(content);
      setLastSaved(new Date());
    } catch (error) {
      console.error('Immediate save failed:', error);
    } finally {
      setIsSaving(false);
    }
  }, [onChange]);

  // Save current state to history
  const saveToHistory = useCallback(() => {
    const currentState = pages.map(p => p.content);
    setHistory(prev => {
      const newHistory = prev.slice(0, historyIndex + 1);
      newHistory.push(currentState);
      return newHistory;
    });
    setHistoryIndex(prev => prev + 1);
  }, [pages, historyIndex]);

  // Update parent component when pages change with immediate save for critical changes
  useEffect(() => {
    const content = {
      type: 'rich_text',
      html: pages.map(p => p.content).join('<div class="page-break"></div>'),
      pages: pages,
      metadata: {
        version: 1,
        created_with: 'AnthroBatch',
        last_modified: new Date().toISOString(),
        collaborators: presenceUsers.length
      }
    };
    
    // Use immediate save for structural changes, regular auto-save for content
    if (pages.length !== (initialContent?.pages?.length || 1)) {
      immediateAutoSave(content);
    } else {
      autoSave(content);
    }
  }, [pages, autoSave, immediateAutoSave, presenceUsers.length, initialContent?.pages?.length]);

  // Update editor content when switching pages
  useEffect(() => {
    if (editorRef.current && currentPage) {
      setIsUpdatingContent(true);
      editorRef.current.innerHTML = currentPage.content || '';
      setIsUpdatingContent(false);
      
      // Update presence - SIMPLIFIED without avatar_url
      if (channelRef.current && user) {
        channelRef.current.track({
          userId: user.id,
          userName: `${profile?.first_name || ''} ${profile?.last_name || ''}`.trim() || user.email?.split('@')[0] || 'Anonymous',
          userColor: userColor,
          online_at: new Date().toISOString()
        });
      }
      
      setTimeout(() => {
        if (editorRef.current) {
          editorRef.current.focus();
          updateFormatState();
        }
      }, 100);
    }
  }, [currentPageIndex, user, userColor, profile?.first_name, profile?.last_name]);

  // Initialize editor content on mount
  useEffect(() => {
    if (editorRef.current && currentPage) {
      editorRef.current.innerHTML = currentPage.content || '';
      editorRef.current.focus();
    }
  }, []);

  const currentPage = pages[currentPageIndex];

  // Rest of the component methods remain the same...
  const execCommand = (command: string, value?: string) => {
    if (!editorRef.current) return;
    
    editorRef.current.focus();
    
    // Special handling for lists
    if (command === 'insertUnorderedList' || command === 'insertOrderedList') {
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        document.execCommand(command, false, value);
        
        setTimeout(() => {
          if (editorRef.current) {
            handlePageContentChange(editorRef.current.innerHTML);
            updateFormatState();
            editorRef.current.focus();
          }
        }, 10);
      } else {
        const sel = window.getSelection();
        if (sel && editorRef.current) {
          const range = document.createRange();
          range.selectNodeContents(editorRef.current);
          range.collapse(false);
          sel.removeAllRanges();
          sel.addRange(range);
          
          document.execCommand(command, false, value);
          
          setTimeout(() => {
            if (editorRef.current) {
              handlePageContentChange(editorRef.current.innerHTML);
              updateFormatState();
              editorRef.current.focus();
            }
          }, 10);
        }
      }
    } else {
      document.execCommand(command, false, value);
      if (editorRef.current) {
        handlePageContentChange(editorRef.current.innerHTML);
        updateFormatState();
      }
    }
  };

  const updateFormatState = () => {
    setActiveFormats({
      bold: document.queryCommandState('bold'),
      italic: document.queryCommandState('italic'),
      underline: document.queryCommandState('underline'),
      unorderedList: document.queryCommandState('insertUnorderedList'),
      orderedList: document.queryCommandState('insertOrderedList')
    });
    
    // Update font size based on current selection
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0 && editorRef.current) {
      const range = selection.getRangeAt(0);
      let element: Node | null = range.startContainer;
      
      // Find the nearest element with font-size
      while (element && element !== editorRef.current) {
        if (element.nodeType === Node.ELEMENT_NODE) {
          const elementNode = element as HTMLElement;
          const inlineStyle = elementNode.style?.fontSize;
          
          if (inlineStyle) {
            if (inlineStyle === '28px') {
              setFontSize('header1');
              return;
            } else if (inlineStyle === '22px') {
              setFontSize('header2');
              return;
            } else if (inlineStyle === '16px') {
              setFontSize('normal');
              return;
            }
          }
        }
        element = element.parentNode;
      }
      
      setFontSize('normal');

      // Broadcast accurate cursor position
      broadcastCursor(selection);
    }
  };

  const handlePageContentChange = (content: string) => {
    if (isUpdatingContent) return;
    
    const newPages = [...pages];
    newPages[currentPageIndex] = {
      ...currentPage,
      content
    };
    setPages(newPages);
    
    // Broadcast changes to other users (only if connected)
    if (connectionStatus === 'connected') {
      broadcastContentChange(newPages, currentPageIndex, 'full_sync');
    }
    
    // Handle typing indicator
    if (!isTyping) {
      setIsTyping(true);
      if (connectionStatus === 'connected') {
        broadcastTyping(true);
      }
    }
    
    // Clear typing indicator after 2 seconds
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    typingTimeoutRef.current = setTimeout(() => {
      setIsTyping(false);
      if (connectionStatus === 'connected') {
        broadcastTyping(false);
      }
    }, 2000);
    
    // Force immediate auto-save for every change
    setTimeout(() => {
      updateFormatState();
      checkPageOverflow();
      
      // Trigger immediate save
      const content = {
        type: 'rich_text',
        html: newPages.map(p => p.content).join('<div class="page-break"></div>'),
        pages: newPages,
        metadata: {
          version: 1,
          created_with: 'AnthroBatch',
          last_modified: new Date().toISOString(),
          collaborators: presenceUsers.length
        }
      };
      immediateAutoSave(content);
    }, 100);
  };

  const addNewPage = () => {
    const newPage: Page = {
      id: `page-${Date.now()}`,
      content: ''
    };
    const newPages = [...pages, newPage];
    setPages(newPages);
    setCurrentPageIndex(newPages.length - 1);
    saveToHistory();
    
    // Broadcast new page
    broadcastContentChange(newPages, newPages.length - 1, 'full_sync');
  };

  const deletePage = (index: number) => {
    if (pages.length === 1) return;
    
    const newPages = pages.filter((_, i) => i !== index);
    setPages(newPages);
    
    if (currentPageIndex >= newPages.length) {
      setCurrentPageIndex(newPages.length - 1);
    }
    saveToHistory();
    
    // Broadcast page deletion
    broadcastContentChange(newPages, Math.min(currentPageIndex, newPages.length - 1), 'full_sync');
  };

  const navigateToPage = (index: number) => {
    if (index >= 0 && index < pages.length) {
      setCurrentPageIndex(index);
      setTimeout(() => {
        if (editorRef.current) {
          editorRef.current.focus();
        }
      }, 100);
    }
  };

  const undo = () => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      const previousState = history[newIndex];
      const restoredPages = previousState.map((content, idx) => ({
        id: pages[idx]?.id || `page-${Date.now()}-${idx}`,
        content
      }));
      setPages(restoredPages);
      setHistoryIndex(newIndex);
      
      // Broadcast undo
      broadcastContentChange(restoredPages, currentPageIndex, 'full_sync');
    }
  };

  const redo = () => {
    if (historyIndex < history.length - 1) {
      const newIndex = historyIndex + 1;
      const nextState = history[newIndex];
      const restoredPages = nextState.map((content, idx) => ({
        id: pages[idx]?.id || `page-${Date.now()}-${idx}`,
        content
      }));
      setPages(restoredPages);
      setHistoryIndex(newIndex);
      
      // Broadcast redo
      broadcastContentChange(restoredPages, currentPageIndex, 'full_sync');
    }
  };

  const getFontSizeValue = (size?: string) => {
    const targetSize = size || fontSize;
    switch (targetSize) {
      case 'header1': return '28px';
      case 'header2': return '22px';
      case 'normal': 
      default: return '16px';
    }
  };

  const applyFontSize = (targetSize?: string) => {
    if (!editorRef.current) return;
    
    editorRef.current.focus();
    const selection = window.getSelection();
    const fontSizeValue = getFontSizeValue(targetSize);
    
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      
      if (!selection.isCollapsed) {
        try {
          const span = document.createElement('span');
          span.style.fontSize = fontSizeValue;
          
          const contents = range.extractContents();
          span.appendChild(contents);
          range.insertNode(span);
          
          const newRange = document.createRange();
          newRange.selectNodeContents(span);
          selection.removeAllRanges();
          selection.addRange(newRange);
          
        } catch (e) {
          console.error('Error applying font size to selection:', e);
          document.execCommand('fontSize', false, '7');
          setTimeout(() => {
            if (editorRef.current) {
              const fontElements = editorRef.current.querySelectorAll('font[size="7"]');
              fontElements.forEach(font => {
                const span = document.createElement('span');
                span.style.fontSize = fontSizeValue;
                span.innerHTML = font.innerHTML;
                if (font.parentNode) {
                  font.parentNode.replaceChild(span, font);
                }
              });
              handlePageContentChange(editorRef.current.innerHTML);
            }
          }, 0);
        }
      } else {
        const span = document.createElement('span');
        span.style.fontSize = fontSizeValue;
        span.textContent = '\u00A0';
        
        range.insertNode(span);
        
        if (span.firstChild) {
          const newRange = document.createRange();
          newRange.setStart(span.firstChild, 1);
          newRange.collapse(true);
          selection.removeAllRanges();
          selection.addRange(newRange);
        }
      }
      
      if (editorRef.current) {
        handlePageContentChange(editorRef.current.innerHTML);
        updateFormatState();
      }
    }
  };

  const checkPageOverflow = () => {
    if (editorRef.current) {
      const pageHeight = 9 * 96;
      const contentHeight = editorRef.current.scrollHeight;
      
      if (contentHeight > pageHeight) {
        addNewPage();
      }
    }
  };

  // Accurate cursor rendering using real coordinates
  const renderLiveCursors = () => {
    const currentPageCursors = cursors.filter(c => c.pageIndex === currentPageIndex);
    
    return currentPageCursors.map(cursor => {
      // Calculate cursor age for fade effect
      const age = new Date().getTime() - new Date(cursor.timestamp).getTime();
      const opacity = Math.max(0.3, 1 - (age / 5000)); // Fade over 5 seconds
      
      return (
        <div 
          key={cursor.userId} 
          className="absolute pointer-events-none z-50 transition-opacity duration-200"
          style={{ opacity }}
        >
          {/* Cursor line */}
          <div
            className="absolute w-0.5 animate-pulse"
            style={{
              backgroundColor: cursor.userColor,
              left: `${Math.max(0, cursor.x)}px`,
              top: `${Math.max(0, cursor.y)}px`,
              height: '20px',
              boxShadow: `0 0 4px ${cursor.userColor}40`
            }}
          />
          
          {/* User name label */}
          <div
            className="absolute text-xs text-white px-2 py-1 rounded-md font-medium shadow-lg whitespace-nowrap transform -translate-y-full -translate-x-1/2 border border-white border-opacity-20"
            style={{
              backgroundColor: cursor.userColor,
              left: `${Math.max(0, cursor.x)}px`,
              top: `${Math.max(0, cursor.y - 5)}px`,
            }}
          >
            {cursor.userName}
            <div 
              className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-2 border-r-2 border-t-2 border-transparent"
              style={{ borderTopColor: cursor.userColor }}
            />
          </div>
        </div>
      );
    });
  };

  // Connection status indicator
  const getConnectionStatusColor = () => {
    switch (connectionStatus) {
      case 'connected': return 'text-green-500';
      case 'reconnecting': return 'text-yellow-500';
      case 'disconnected': return 'text-red-500';
      default: return 'text-gray-500';
    }
  };

  const getConnectionStatusIcon = () => {
    switch (connectionStatus) {
      case 'connected': return <Wifi className="w-4 h-4 text-green-500" />;
      case 'reconnecting': return <Wifi className="w-4 h-4 text-yellow-500 animate-pulse" />;
      case 'disconnected': return <WifiOff className="w-4 h-4 text-red-500" />;
      default: return <WifiOff className="w-4 h-4 text-gray-500" />;
    }
  };

  return (
    <div className="flex h-full bg-white">
      <div className="flex-1 flex flex-col">
        {/* Enhanced Collaboration Header */}
        <div className="bg-white border-b border-gray-200 p-4 shadow-sm">
          <div className="flex items-center justify-between mb-4 pb-3 border-b border-gray-200">
            <div className="flex items-center gap-6">
              <PresenceIndicator 
                users={presenceUsers} 
                currentUserId={user?.id || ''} 
                className="text-sm"
              />
            </div>

            <div className="flex items-center gap-6">
              <div className="flex items-center gap-3">
                {isSaving ? (
                  <>
                    <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                    <span className="text-sm text-blue-600 font-medium">Saving...</span>
                  </>
                ) : lastSaved ? (
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <span className="text-sm text-green-600">
                      Auto-saved {lastSaved.toLocaleTimeString()}
                    </span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse"></div>
                    <span className="text-sm text-yellow-600">Auto-saving enabled</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Toolbar */}
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2 border-r border-gray-300 pr-4">
              <button
                onClick={undo}
                disabled={historyIndex <= 0}
                className="p-2 rounded-sm hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-sm hover:shadow group"
                title="Undo"
              >
                <Undo className="h-4 w-4 group-hover:text-blue-600 transition-colors" />
              </button>
              <button
                onClick={redo}
                disabled={historyIndex >= history.length - 1}
                className="p-2 rounded-sm hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-sm hover:shadow group"
                title="Redo"
              >
                <Redo className="h-4 w-4 group-hover:text-blue-600 transition-colors" />
              </button>
            </div>

            <div className="flex items-center gap-2 border-r border-gray-300 pr-4">
              <Type className="h-4 w-4 text-gray-600" />
              <select
                value={fontSize}
                onChange={(e) => {
                  const newSize = e.target.value as any;
                  setFontSize(newSize);
                  setTimeout(() => {
                    applyFontSize(newSize);
                  }, 0);
                }}
                className="px-3 py-1.5 border border-gray-200 rounded-sm text-sm font-medium bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200"
              >
                <option value="header1">Header 1</option>
                <option value="header2">Header 2</option>
                <option value="normal">Normal Text</option>
              </select>
            </div>

            <div className="flex items-center gap-2 border-r border-gray-300 pr-4">
              <button
                onClick={() => execCommand('bold')}
                className={`p-2 rounded-sm font-bold transition-all duration-200 ${
                  activeFormats.bold
                    ? 'bg-blue-100 text-blue-700 shadow-sm'
                    : 'hover:bg-white hover:shadow-sm'
                }`}
                title="Bold"
              >
                <Bold className="h-4 w-4" />
              </button>
              <button
                onClick={() => execCommand('italic')}
                className={`p-2 rounded-sm italic transition-all duration-200 ${
                  activeFormats.italic
                    ? 'bg-blue-100 text-blue-700 shadow-sm'
                    : 'hover:bg-white hover:shadow-sm'
                }`}
                title="Italic"
              >
                <Italic className="h-4 w-4" />
              </button>
              <button
                onClick={() => execCommand('underline')}
                className={`p-2 rounded-sm underline transition-all duration-200 ${
                  activeFormats.underline
                    ? 'bg-blue-100 text-blue-700 shadow-sm'
                    : 'hover:bg-white hover:shadow-sm'
                }`}
                title="Underline"
              >
                <Underline className="h-4 w-4" />
              </button>
            </div>

            <div className="flex items-center gap-1 border-r border-gray-300 pr-4">
              <button
                onClick={() => execCommand('justifyLeft')}
                className="p-2 rounded hover:bg-gray-200 transition-colors"
                title="Align left"
              >
                <AlignLeft className="h-4 w-4" />
              </button>
              <button
                onClick={() => execCommand('justifyCenter')}
                className="p-2 rounded hover:bg-gray-200 transition-colors"
                title="Align center"
              >
                <AlignCenter className="h-4 w-4" />
              </button>
              <button
                onClick={() => execCommand('justifyRight')}
                className="p-2 rounded hover:bg-gray-200 transition-colors"
                title="Align right"
              >
                <AlignRight className="h-4 w-4" />
              </button>
            </div>

            <div className="flex items-center gap-1">
              <button
                onClick={() => execCommand('insertUnorderedList')}
                className={`p-2 rounded transition-colors ${
                  activeFormats.unorderedList
                    ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                    : 'hover:bg-gray-200'
                }`}
                title="Bullet list"
              >
                <List className="h-4 w-4" />
              </button>
              <button
                onClick={() => execCommand('insertOrderedList')}
                className={`p-2 rounded transition-colors ${
                  activeFormats.orderedList
                    ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                    : 'hover:bg-gray-200'
                }`}
                title="Numbered list"
              >
                <ListOrdered className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Page Content Area */}
        <div className="flex-1 overflow-auto bg-white p-8">
          <div className="flex justify-center">
            <div className="max-w-4xl space-y-8">
              {(singlePageMode ? [pages[currentPageIndex]] : pages).filter(Boolean).map((page, index) => {
                const actualIndex = singlePageMode ? currentPageIndex : index;
                return (
                <div
                  key={page.id}
                  className="bg-white shadow-lg mx-auto relative transition-all duration-200 hover:shadow-xl"
                  style={{ minHeight: '11in', padding: '1in', width: '8.5in' }}
                >
                  {actualIndex === currentPageIndex ? (
                    <div className="relative">
                      {renderLiveCursors()}
                      
                      <div
                        ref={editorRef}
                        contentEditable
                        className="focus:outline-none notebook-editor relative z-10"
                        style={{ 
                          minHeight: '9in', 
                          fontSize: '16px',
                          lineHeight: '1.6',
                          maxHeight: '9in',
                          overflowY: 'hidden'
                        }}
                        onInput={(e) => {
                          if (!isUpdatingContent) {
                            handlePageContentChange(e.currentTarget.innerHTML);
                          }
                        }}
                        onPaste={(e) => {
                          setTimeout(() => updateFormatState(), 10);
                        }}
                        onKeyUp={() => {
                          saveToHistory();
                          updateFormatState();
                        }}
                        onMouseUp={() => {
                          updateFormatState();
                          // Also broadcast cursor on mouse up (when user clicks)
                          const selection = window.getSelection();
                          if (selection) {
                            broadcastCursor(selection);
                          }
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            const selection = window.getSelection();
                            if (selection && selection.rangeCount > 0) {
                              const range = selection.getRangeAt(0);
                              let listItem: Node | null = range.startContainer;
                              
                              while (listItem && listItem !== editorRef.current) {
                                if (listItem.nodeType === Node.ELEMENT_NODE && 
                                    (listItem as HTMLElement).tagName === 'LI') {
                                  const li = listItem as HTMLLIElement;
                                  
                                  const isEmpty = li.textContent?.trim() === '' || 
                                                (li.innerHTML === '<br>' || li.innerHTML === '<br/>');
                                  
                                  if (isEmpty) {
                                    e.preventDefault();
                                    const parentList = li.parentElement;
                                    if (parentList && (parentList.tagName === 'UL' || parentList.tagName === 'OL')) {
                                      if (parentList.tagName === 'UL') {
                                        document.execCommand('insertUnorderedList', false);
                                      } else {
                                        document.execCommand('insertOrderedList', false);
                                      }
                                      document.execCommand('insertHTML', false, '<br>');
                                      if (editorRef.current) {
                                        handlePageContentChange(editorRef.current.innerHTML);
                                      }
                                    }
                                  }
                                  break;
                                }
                                listItem = listItem.parentNode;
                              }
                            }
                            
                            setTimeout(() => updateFormatState(), 10);
                          }
                          
                          if (e.ctrlKey || e.metaKey) {
                            switch (e.key) {
                              case 'b':
                                e.preventDefault();
                                execCommand('bold');
                                break;
                              case 'i':
                                e.preventDefault();
                                execCommand('italic');
                                break;
                              case 'u':
                                e.preventDefault();
                                execCommand('underline');
                                break;
                            }
                          }
                        }}
                        onFocus={() => {
                          setCurrentPageIndex(index);
                          updateFormatState();
                        }}
                      />
                    </div>
                  ) : (
                    <div
                      className="cursor-pointer hover:bg-gray-50 transition-colors border-l-4 border-transparent hover:border-blue-400"
                      style={{ 
                        minHeight: '9in', 
                        fontSize: '16px',
                        lineHeight: '1.6'
                      }}
                      dangerouslySetInnerHTML={{ __html: page.content }}
                      onClick={() => navigateToPage(actualIndex)}
                    />
                  )}
                  
                  {/* Simplified page indicator */}
                  <div className="flex items-center justify-between text-xs text-gray-400 mt-4 pt-4 border-t border-gray-100">
                    <span>Page {actualIndex + 1} of {pages.length}</span>
                  </div>
                </div>
              )})}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};